import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, qs } from '../lib/api'
import { ago } from '../lib/format'
import type { Device, ListResponse } from '../lib/types'
import { Chip, Spinner } from './ui'
import { useSearch } from './DataTable'

/**
 * A searchable picker for a vehicle's hardware.
 *
 * A reseller's stock pool runs to thousands of units, and a fitter rarely
 * knows an IMEI by heart, so a <select> does not work: it was capped at 200
 * rows and showed the number alone. This asks the server as you type —
 * matching IMEI, serial, model, SIM and notes — and shows only units that are
 * not yet fitted to anything, so a pick can never silently take hardware off
 * another vehicle. The unit already fitted to the vehicle being edited stays
 * selectable at the top.
 */

export type PickedDevice = { id: string; imei: string; modelName?: string; vendorName?: string }

/** How a slot's units are described in messages. */
const CAPABILITY_NOUN: Record<'tracking' | 'video' | 'recovery' | 'any', string> = {
  tracking: 'tracking unit',
  video: 'camera unit',
  recovery: 'recovery unit',
  any: 'unit',
}

export function DevicePicker({
  value,
  current,
  exclude = [],
  capability,
  onChange,
  placeholder = 'Search IMEI, serial, model or SIM…',
}: {
  /** The chosen device id, or '' for none. */
  value: string
  /** The unit already fitted (when editing): always offered, even though it is not "unfitted". */
  current?: PickedDevice
  /** Ids chosen in the sibling slots (camera, recovery), hidden here. */
  exclude?: string[]
  /** Only units that can do this job (tracking, video, recovery), per their device settings. */
  capability?: 'tracking' | 'video' | 'recovery'
  onChange: (id: string, device?: PickedDevice) => void
  placeholder?: string
}) {
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const [picked, setPicked] = useState<PickedDevice | undefined>(current && current.id === value ? current : undefined)
  const search = useSearch(250)
  const root = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLInputElement>(null)
  const list = useRef<HTMLUListElement>(null)

  // Editing: the vehicle's own unit arrives after the first render.
  useEffect(() => {
    if (current && current.id === value) setPicked(current)
  }, [current, value])

  const results = useQuery({
    queryKey: ['devices', 'pick', capability ?? '', search.debounced],
    queryFn: () =>
      api<ListResponse<Device>>(
        `/devices${qs({ unassigned: true, capability, q: search.debounced, limit: 50 })}`,
      ),
    enabled: open,
    staleTime: 15_000,
  })

  const term = search.debounced.trim().toLowerCase()
  const currentMatches =
    current &&
    (term === '' ||
      current.imei.toLowerCase().includes(term) ||
      `${current.vendorName ?? ''} ${current.modelName ?? ''}`.toLowerCase().includes(term))
  const options: PickedDevice[] = [
    ...(currentMatches ? [current!] : []),
    ...(results.data?.items ?? [])
      .filter((d) => d.id !== current?.id && !exclude.includes(d.id))
      .map((d) => ({ id: d.id, imei: d.imei, modelName: d.modelName, vendorName: d.vendorName, _d: d })),
  ]

  useEffect(() => setActive(0), [search.debounced, open])

  // Close on a click anywhere else.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  useEffect(() => {
    list.current?.children[active]?.scrollIntoView({ block: 'nearest' })
  }, [active])

  function choose(d: PickedDevice) {
    setPicked(d)
    onChange(d.id, d)
    search.setValue('')
    setOpen(false)
  }

  function clear() {
    setPicked(undefined)
    onChange('')
    search.setValue('')
    input.current?.focus()
    setOpen(true)
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true)
      return
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActive((i) => Math.min(i + 1, options.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setActive((i) => Math.max(i - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        if (options[active]) choose(options[active]!)
        break
      case 'Escape':
        setOpen(false)
        break
      case 'Backspace':
        if (picked && search.value === '') clear()
        break
    }
  }

  const label = picked ? `${picked.imei}${picked.modelName ? ` · ${picked.vendorName ?? ''} ${picked.modelName}`.trimEnd() : ''}` : ''

  return (
    <div ref={root} className="relative">
      <div className="relative">
        <input
          ref={input}
          className="field pr-7 tnum"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          autoComplete="off"
          value={open || !picked ? search.value : label}
          placeholder={picked ? label : placeholder}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onChange={(e) => {
            search.setValue(e.target.value)
            if (!open) setOpen(true)
          }}
          onKeyDown={onKey}
        />
        {(picked || search.value) && (
          <button
            type="button"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 btn btn-ghost btn-sm px-1"
            title="Clear"
            aria-label="Clear device"
            onMouseDown={(e) => e.preventDefault()}
            onClick={clear}
          >
            <span aria-hidden className="text-[12px] leading-none">✕</span>
          </button>
        )}
      </div>

      {open && (
        <ul
          ref={list}
          role="listbox"
          className="absolute left-0 right-0 z-50 mt-1 max-h-64 overflow-y-auto rounded-[3px] py-1"
          style={{
            background: 'var(--color-panel)',
            border: '1px solid var(--color-rule-bright)',
            boxShadow: 'var(--shadow-pop)',
          }}
        >
          {results.isLoading && (
            <li className="px-3 py-2 text-[12px] text-[var(--color-ink-3)] flex items-center gap-2">
              <Spinner /> Searching…
            </li>
          )}
          {!results.isLoading && options.length === 0 && (
            <li className="px-3 py-2 text-[12px] text-[var(--color-ink-3)]">
              {term
                ? `No unfitted ${CAPABILITY_NOUN[capability ?? 'any']} matches that.`
                : capability
                  ? `No unfitted ${CAPABILITY_NOUN[capability]} in this account — tick it under “What this unit does” on the Devices page, or transfer one here.`
                  : 'No unfitted units in this account — transfer hardware here first.'}
            </li>
          )}
          {options.map((d, i) => {
            const full = (d as PickedDevice & { _d?: Device })._d
            const isCurrent = current?.id === d.id
            return (
              <li
                key={d.id}
                role="option"
                aria-selected={d.id === value}
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActive(i)}
                onClick={(e) => {
                  // Inside a <label>: stop the click reaching it, or it would
                  // re-focus the input and reopen the list we just closed.
                  e.preventDefault()
                  choose(d)
                }}
                className="px-3 py-1.5 cursor-pointer flex items-center gap-3"
                style={{ background: i === active ? 'var(--accent-dim)' : 'transparent' }}
              >
                <div className="min-w-0 grow">
                  <div className="tnum text-[12.5px] text-[var(--color-ink)]">
                    {d.imei}
                    {isCurrent && <span className="text-[var(--color-ink-4)]"> (current)</span>}
                  </div>
                  <div className="text-[11px] text-[var(--color-ink-3)] truncate">
                    {[
                      d.modelName ? `${d.vendorName ?? ''} ${d.modelName}`.trim() : full?.protocol,
                      full?.source ? `via ${full.source} gateway` : undefined,
                      full?.serialNo ? `S/N ${full.serialNo}` : undefined,
                    ]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </div>
                </div>
                {full && (
                  <div className="shrink-0 flex items-center gap-2">
                    <span className="text-[10px] text-[var(--color-ink-4)] tnum">{ago(full.lastSeenAt)}</span>
                    <Chip tone={full.status === 'active' ? 'ok' : full.status === 'assigned' ? 'accent' : 'muted'}>
                      {full.status}
                    </Chip>
                  </div>
                )}
              </li>
            )
          })}
          {!results.isLoading && (results.data?.total ?? 0) > 50 && (
            <li className="px-3 py-1.5 text-[10.5px] text-[var(--color-ink-4)]">
              Showing 50 of {results.data!.total} — keep typing to narrow it down.
            </li>
          )}
        </ul>
      )}
    </div>
  )
}
